import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { HermesSkillEditor } from "@/components/hermes/HermesSkillEditor";
import { HermesSkillFileTree } from "@/components/hermes/HermesSkillFileTree";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  createHermesApi,
  hermesDbSkillSourceLabel,
  type HermesDbSkill,
  type HermesSkillInstallTarget,
  type HermesSkillScope,
} from "@/lib/hermes-api";
import {
  canDeleteHermesSkill,
  canEditHermesSkill,
  canToggleHermesSkillEnabled,
  isPlatformStaff,
  useHermesCanManageAccountSkills,
} from "@/lib/hermes-skills-permissions";
import {
  computeSkillPackageChanges,
  DEFAULT_NEW_SKILL_BODY,
  HERMES_SKILL_ROOT_FILE,
  isAllowedSkillPackagePath,
  skillPackageIsDirty,
} from "@/lib/hermes-skills-package";
import { cn } from "@/lib/utils";

type WorkbenchTab = "skills" | "toolsets" | "mcp";
type ScopeFilter = "all" | HermesSkillScope;

type SkillSelection =
  | { kind: "none" }
  | {
      kind: "new";
      target: HermesSkillInstallTarget;
      metaName: string;
      sourceUrl: string | null;
      files: Record<string, string>;
      lastSavedFiles: Record<string, string>;
    }
  | {
      kind: "existing";
      skill: HermesDbSkill;
      files: Record<string, string>;
      lastSavedFiles: Record<string, string>;
    };

function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

function cloneFiles(files: Record<string, string>): Record<string, string> {
  return { ...files };
}

function EnableSwitch({
  checked,
  disabled,
  onCheckedChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 rounded-full border transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "border-primary bg-primary" : "border-input bg-muted",
      )}
      onClick={() => {
        if (!disabled) onCheckedChange(!checked);
      }}
    >
      <span
        className={cn(
          "pointer-events-none block size-4 translate-x-0.5 rounded-full bg-background shadow-sm transition-transform",
          checked && "translate-x-[18px]",
        )}
      />
    </button>
  );
}

function visibilityBadge(skill: HermesDbSkill): string {
  if (skill.visibility === "system") return "系统";
  if (skill.visibility === "account") return "企业";
  return "个人";
}

function PlaceholderPane({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

type HermesSkillsWorkbenchProps = {
  accountId: string;
};

export function HermesSkillsWorkbench({ accountId }: HermesSkillsWorkbenchProps) {
  const { user } = useAuth();
  const platformStaff = isPlatformStaff(user);
  const { canManage: canManageAccount, ready: permissionsReady } =
    useHermesCanManageAccountSkills(accountId);

  const [workbenchTab, setWorkbenchTab] = useState<WorkbenchTab>("skills");
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");
  const [search, setSearch] = useState("");
  const [skills, setSkills] = useState<HermesDbSkill[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selection, setSelection] = useState<SkillSelection>({ kind: "none" });
  const [selectedPath, setSelectedPath] = useState<string | null>(HERMES_SKILL_ROOT_FILE);
  const [detailLoading, setDetailLoading] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busySkillId, setBusySkillId] = useState<number | null>(null);
  const [originDiffText, setOriginDiffText] = useState<string | null>(null);
  const [originDiffLoading, setOriginDiffLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);

  const permissionOptions = useMemo(
    () => ({ isPlatformStaff: platformStaff, canManageAccount }),
    [canManageAccount, platformStaff],
  );

  const loadSkills = useCallback(async () => {
    const hermes = createHermesApi(accountId);
    setLoadingList(true);
    try {
      const { skills: items } = await hermes.listDbSkills("all");
      setSkills(items);
    } catch (error) {
      toast.error(apiErrorMessage(error, "加载技能列表失败"));
    } finally {
      setLoadingList(false);
    }
  }, [accountId]);

  useEffect(() => {
    void loadSkills();
  }, [loadSkills]);

  const filteredSkills = useMemo(() => {
    const query = search.trim().toLowerCase();
    return skills.filter((skill) => {
      if (scopeFilter !== "all" && skill.visibility !== scopeFilter) return false;
      if (!query) return true;
      const haystack = [skill.name, skill.title, skill.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [scopeFilter, search, skills]);

  const selectedSkillId =
    selection.kind === "existing" ? selection.skill.id : selection.kind === "new" ? "new" : null;

  const currentFiles = selection.kind === "none" ? {} : selection.files;
  const lastSavedFiles = selection.kind === "none" ? {} : selection.lastSavedFiles;
  const isDirty =
    selection.kind !== "none" && skillPackageIsDirty(currentFiles, lastSavedFiles);

  const canEditSelection = useMemo(() => {
    if (selection.kind === "new") {
      return selection.target === "user" || canManageAccount;
    }
    if (selection.kind === "existing") {
      return canEditHermesSkill(selection.skill, permissionOptions);
    }
    return false;
  }, [canManageAccount, permissionOptions, selection]);

  async function loadSkillDetail(skill: HermesDbSkill) {
    const hermes = createHermesApi(accountId);
    setDetailLoading(true);
    setOriginDiffText(null);
    try {
      const { skill: detail } = await hermes.getDbSkill(skill.id);
      const files: Record<string, string> = {};
      await Promise.all(
        detail.files.map(async ({ path }) => {
          const { body } = await hermes.getDbSkillFile(skill.id, path);
          files[path] = body;
        }),
      );
      const snapshot = cloneFiles(files);
      setSelection({
        kind: "existing",
        skill: detail,
        files: snapshot,
        lastSavedFiles: cloneFiles(snapshot),
      });
      setSelectedPath(
        files[HERMES_SKILL_ROOT_FILE] !== undefined ? HERMES_SKILL_ROOT_FILE : Object.keys(files)[0] ?? null,
      );
    } catch (error) {
      toast.error(apiErrorMessage(error, "加载技能详情失败"));
    } finally {
      setDetailLoading(false);
    }
  }

  function startNewSkill(target: HermesSkillInstallTarget) {
    const files = { [HERMES_SKILL_ROOT_FILE]: DEFAULT_NEW_SKILL_BODY };
    setSelection({
      kind: "new",
      target,
      metaName: "",
      sourceUrl: null,
      files,
      lastSavedFiles: {},
    });
    setSelectedPath(HERMES_SKILL_ROOT_FILE);
    setOriginDiffText(null);
    setImportUrl("");
  }

  function updateFileBody(path: string, body: string) {
    setSelection((current) => {
      if (current.kind === "none") return current;
      return { ...current, files: { ...current.files, [path]: body } };
    });
  }

  function addFile(path: string) {
    if (!isAllowedSkillPackagePath(path)) {
      toast.error("不允许此路径；仅支持 SKILL.md 与 references/templates/examples 下的文本文件");
      return;
    }
    setSelection((current) => {
      if (current.kind === "none") return current;
      if (current.files[path] !== undefined) return current;
      return { ...current, files: { ...current.files, [path]: "" } };
    });
  }

  function deleteFile(path: string) {
    if (path === HERMES_SKILL_ROOT_FILE) {
      toast.error("不能删除 SKILL.md");
      return;
    }
    setSelection((current) => {
      if (current.kind === "none") return current;
      const next = { ...current.files };
      delete next[path];
      return { ...current, files: next };
    });
    if (selectedPath === path) {
      setSelectedPath(HERMES_SKILL_ROOT_FILE);
    }
  }

  async function handleImportUrl(event: React.FormEvent) {
    event.preventDefault();
    const url = importUrl.trim();
    if (!url || importing) return;

    const hermes = createHermesApi(accountId);
    setImporting(true);
    try {
      const draft = await hermes.importSkillUrl(url);
      const files = Object.fromEntries(draft.files.map((file) => [file.path, file.body]));
      if (!files[HERMES_SKILL_ROOT_FILE]) {
        toast.error("链接内容缺少 SKILL.md，无法填充");
        return;
      }

      if (draft.stripped_paths.length > 0) {
        toast.message(`已自动移除 ${draft.stripped_paths.length} 个不允许的路径`, {
          description: draft.stripped_paths.slice(0, 5).join("、") + (draft.stripped_paths.length > 5 ? "…" : ""),
        });
      }

      if (selection.kind === "existing") {
        setSelection({
          ...selection,
          files,
          lastSavedFiles: cloneFiles(selection.lastSavedFiles),
        });
      } else {
        const target: HermesSkillInstallTarget =
          selection.kind === "new" ? selection.target : scopeFilter === "account" ? "account" : "user";
        setSelection({
          kind: "new",
          target,
          metaName: draft.meta.name,
          sourceUrl: url,
          files,
          lastSavedFiles: selection.kind === "new" ? selection.lastSavedFiles : {},
        });
      }

      setSelectedPath(HERMES_SKILL_ROOT_FILE);
      toast.success("已填充到草稿，记得点保存");
    } catch (error) {
      toast.error(apiErrorMessage(error, "链接填充失败"));
    } finally {
      setImporting(false);
    }
  }

  async function handleSave() {
    if (selection.kind === "none" || saving) return;
    if (!currentFiles[HERMES_SKILL_ROOT_FILE]?.trim()) {
      toast.error("SKILL.md 不能为空");
      return;
    }

    const hermes = createHermesApi(accountId);
    setSaving(true);
    try {
      if (selection.kind === "new") {
        const filesPayload = Object.entries(currentFiles).map(([path, body]) => ({ path, body }));
        const { skill } = await hermes.createDbSkill({
          target: selection.target,
          files: filesPayload,
          meta: {
            name: selection.metaName || undefined,
            source_url: selection.sourceUrl,
          },
        });
        toast.success(`已保存「${skill.title?.trim() || skill.name}」`);
        await loadSkills();
        await loadSkillDetail(skill);
        return;
      }

      const { filesUpsert, filesDelete } = computeSkillPackageChanges(
        currentFiles,
        lastSavedFiles,
      );
      const { skill } = await hermes.updateDbSkillPackage(selection.skill.id, {
        files_upsert: filesUpsert.length > 0 ? filesUpsert : undefined,
        files_delete: filesDelete.length > 0 ? filesDelete : undefined,
      });
      toast.success("已保存");
      const snapshot = cloneFiles(currentFiles);
      setSelection({
        kind: "existing",
        skill,
        files: snapshot,
        lastSavedFiles: cloneFiles(snapshot),
      });
      setSkills((current) => current.map((item) => (item.id === skill.id ? skill : item)));
    } catch (error) {
      toast.error(apiErrorMessage(error, "保存失败"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteSkill() {
    if (selection.kind !== "existing") return;
    const skill = selection.skill;
    if (!canDeleteHermesSkill(skill, permissionOptions)) return;

    const hermes = createHermesApi(accountId);
    setBusySkillId(skill.id);
    try {
      await hermes.deleteDbSkill(skill.id);
      toast.success("已删除");
      setDeleteConfirmOpen(false);
      setSelection({ kind: "none" });
      setSelectedPath(null);
      setSkills((current) => current.filter((item) => item.id !== skill.id));
    } catch (error) {
      toast.error(apiErrorMessage(error, "删除失败"));
    } finally {
      setBusySkillId(null);
    }
  }

  async function handleRestoreOrigin() {
    if (selection.kind !== "existing" || !platformStaff) return;

    const hermes = createHermesApi(accountId);
    setSaving(true);
    try {
      const { skill } = await hermes.restoreDbSkill(selection.skill.id);
      toast.success("已还原为原始版本");
      setRestoreConfirmOpen(false);
      await loadSkills();
      await loadSkillDetail(skill);
    } catch (error) {
      toast.error(apiErrorMessage(error, "还原失败"));
    } finally {
      setSaving(false);
    }
  }

  async function handleLoadOriginDiff() {
    if (selection.kind !== "existing") return;
    const hermes = createHermesApi(accountId);
    setOriginDiffLoading(true);
    try {
      const { diffs } = await hermes.diffDbSkillOrigin(selection.skill.id);
      const text = diffs
        .filter((entry) => entry.unified)
        .map((entry) => entry.unified)
        .join("\n\n");
      setOriginDiffText(text || "与原始种子一致，无差异");
    } catch (error) {
      toast.error(apiErrorMessage(error, "加载原始差异失败"));
    } finally {
      setOriginDiffLoading(false);
    }
  }

  async function handleToggleListSkill(skill: HermesDbSkill, enabled: boolean) {
    if (!canToggleHermesSkillEnabled(skill, permissionOptions)) return;
    const hermes = createHermesApi(accountId);
    setBusySkillId(skill.id);
    try {
      const { skill: updated } = await hermes.updateDbSkillPackage(skill.id, { enabled });
      setSkills((current) => current.map((item) => (item.id === skill.id ? updated : item)));
      if (selection.kind === "existing" && selection.skill.id === skill.id) {
        setSelection({ ...selection, skill: updated });
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "更新启用状态失败"));
    } finally {
      setBusySkillId(null);
    }
  }

  const newTargetDefault: HermesSkillInstallTarget =
    scopeFilter === "account" ? "account" : "user";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="mb-2">
          <h2 className="text-base font-medium">技能与工具</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            保存后，助手才能按需加载这些工作协议。
          </p>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="工作台">
          {(
            [
              ["skills", "技能"],
              ["toolsets", "工具集"],
              ["mcp", "MCP"],
            ] as const
          ).map(([tab, label]) => (
            <Button
              key={tab}
              type="button"
              role="tab"
              aria-selected={workbenchTab === tab}
              variant={workbenchTab === tab ? "default" : "outline"}
              size="sm"
              onClick={() => setWorkbenchTab(tab)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {workbenchTab === "toolsets" ? (
        <PlaceholderPane
          title="工具集 · 即将推出"
          description="与输入框旁的知识库、联网等工具开关相关；本期仅占位。"
        />
      ) : null}

      {workbenchTab === "mcp" ? (
        <PlaceholderPane
          title="MCP · 即将推出"
          description="外部 MCP 服务接入将在此管理；本期仅占位。"
        />
      ) : null}

      {workbenchTab === "skills" ? (
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-[min(100%,14rem)] shrink-0 flex-col border-r border-border">
            <div className="shrink-0 space-y-2 border-b border-border p-3">
              <div className="flex flex-wrap gap-1">
                {(
                  [
                    ["all", "全部"],
                    ["user", "个人"],
                    ["account", "企业"],
                    ["system", "系统"],
                  ] as const
                ).map(([scope, label]) => (
                  <Button
                    key={scope}
                    type="button"
                    size="sm"
                    variant={scopeFilter === scope ? "default" : "ghost"}
                    className="h-7 px-2 text-xs"
                    onClick={() => setScopeFilter(scope)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <Input
                value={search}
                placeholder="搜索名称或说明…"
                className="h-8 text-xs"
                onChange={(event) => setSearch(event.target.value)}
              />
              <Button
                type="button"
                size="sm"
                className="h-8 w-full gap-1 text-xs"
                disabled={newTargetDefault === "account" && permissionsReady && !canManageAccount}
                onClick={() => startNewSkill(newTargetDefault)}
              >
                <Plus className="size-3.5" />
                新建{newTargetDefault === "account" ? "企业" : "个人"}技能
              </Button>
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <div className="space-y-1 p-2">
                {loadingList ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" />
                    加载中…
                  </div>
                ) : filteredSkills.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs text-muted-foreground">暂无匹配技能</p>
                ) : (
                  filteredSkills.map((skill) => {
                    const title = skill.title?.trim() || skill.name;
                    const isActive = selectedSkillId === skill.id;
                    const canToggle = canToggleHermesSkillEnabled(skill, permissionOptions);
                    return (
                      <div
                        key={skill.id}
                        className={cn(
                          "rounded-lg border p-2 transition-colors",
                          isActive ? "border-primary bg-accent/40" : "border-transparent hover:bg-muted/40",
                        )}
                      >
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() => void loadSkillDetail(skill)}
                        >
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs font-medium leading-tight">{title}</span>
                            <span className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
                              {visibilityBadge(skill)}
                            </span>
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
                            {skill.description?.trim() || "暂无说明"}
                          </p>
                        </button>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">
                            {hermesDbSkillSourceLabel(skill)}
                          </span>
                          <EnableSwitch
                            checked={skill.enabled}
                            disabled={!canToggle || busySkillId === skill.id}
                            label={skill.enabled ? "禁用" : "启用"}
                            onCheckedChange={(enabled) => void handleToggleListSkill(skill, enabled)}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            {selection.kind === "none" ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
                <p className="text-sm font-medium">选择或新建技能</p>
                <p className="text-xs text-muted-foreground">左侧点选或新建技能后，可粘贴链接填充草稿</p>
              </div>
            ) : (
              <>
                <div className="shrink-0 space-y-2 border-b border-border px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 space-y-0.5">
                      <p className="text-sm font-medium">
                        {selection.kind === "new"
                          ? `新建${selection.target === "account" ? "企业" : "个人"}技能`
                          : selection.skill.title?.trim() || selection.skill.name}
                      </p>
                      {selection.kind === "existing" ? (
                        <p className="text-[11px] text-muted-foreground">
                          {visibilityBadge(selection.skill)} · {selection.skill.name}
                          {isDirty ? " · 有未保存变更" : ""}
                        </p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground">草稿 · 保存后才会生效</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {canEditSelection ? (
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          disabled={saving || !isDirty}
                          onClick={() => void handleSave()}
                        >
                          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
                          保存
                        </Button>
                      ) : null}
                      {selection.kind === "existing" &&
                      canDeleteHermesSkill(selection.skill, permissionOptions) ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-xs text-destructive hover:text-destructive"
                          disabled={busySkillId === selection.skill.id}
                          onClick={() => setDeleteConfirmOpen(true)}
                        >
                          <Trash2 className="size-3.5" />
                          删除
                        </Button>
                      ) : null}
                      {selection.kind === "existing" &&
                      selection.skill.visibility === "system" &&
                      platformStaff ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1 text-xs"
                          disabled={saving}
                          onClick={() => setRestoreConfirmOpen(true)}
                        >
                          <RotateCcw className="size-3.5" />
                          还原种子
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {canEditSelection ? (
                    <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void handleImportUrl(event)}>
                      <div className="min-w-[12rem] flex-1 space-y-1">
                        <Label htmlFor="hermes-skill-import-url" className="text-xs">
                          从链接填充
                        </Label>
                        <Input
                          id="hermes-skill-import-url"
                          type="url"
                          placeholder="https://…/SKILL.md"
                          value={importUrl}
                          disabled={importing}
                          className="h-8 text-xs"
                          onChange={(event) => setImportUrl(event.target.value)}
                        />
                      </div>
                      <Button
                        type="submit"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={importing || !importUrl.trim()}
                      >
                        {importing ? <Loader2 className="size-3.5 animate-spin" /> : "填充草稿"}
                      </Button>
                    </form>
                  ) : null}

                  {selection.kind === "existing" &&
                  selection.skill.visibility === "account" &&
                  permissionsReady &&
                  !canManageAccount ? (
                    <p className="text-xs text-muted-foreground">企业技能仅管理员可编辑</p>
                  ) : null}
                  {selection.kind === "existing" &&
                  selection.skill.visibility === "system" &&
                  !platformStaff ? (
                    <p className="text-xs text-muted-foreground">系统技能全员可读；仅平台运营可编辑</p>
                  ) : null}
                </div>

                <div className="flex min-h-0 flex-1">
                  <div className="w-44 shrink-0">
                    <HermesSkillFileTree
                      paths={Object.keys(currentFiles)}
                      selectedPath={selectedPath}
                      canEdit={canEditSelection}
                      onSelect={setSelectedPath}
                      onAddFile={addFile}
                      onDeleteFile={deleteFile}
                    />
                  </div>
                  <HermesSkillEditor
                    selectedPath={selectedPath}
                    content={selectedPath ? (currentFiles[selectedPath] ?? "") : ""}
                    canEdit={canEditSelection}
                    loading={detailLoading}
                    lastSavedFiles={lastSavedFiles}
                    currentFiles={currentFiles}
                    originDiffText={originDiffText}
                    originDiffLoading={originDiffLoading}
                    onChange={(body) => {
                      if (selectedPath) updateFileBody(selectedPath, body);
                    }}
                    onLoadOriginDiff={
                      selection.kind === "existing" && selection.skill.visibility === "system"
                        ? () => void handleLoadOriginDiff()
                        : undefined
                    }
                  />
                </div>
              </>
            )}
          </section>
        </div>
      ) : null}

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open && busySkillId == null) setDeleteConfirmOpen(false);
        }}
        title="删除技能"
        description={
          selection.kind === "existing"
            ? `确定删除技能「${selection.skill.title?.trim() || selection.skill.name}」？此操作不可撤销。`
            : ""
        }
        confirmLabel="删除"
        tone="destructive"
        confirming={busySkillId != null}
        onConfirm={() => void handleDeleteSkill()}
      />

      <ConfirmDialog
        open={restoreConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !saving) setRestoreConfirmOpen(false);
        }}
        title="还原平台种子"
        description="确定还原为平台原始种子？当前对系统技能的修改将丢失。"
        confirmLabel="还原"
        tone="destructive"
        confirming={saving}
        onConfirm={() => void handleRestoreOrigin()}
      />
    </div>
  );
}
